import { useEffect, useRef } from 'react';

export const ScrollDirection = {
    /** @type {'scrollTop'} */ Top: 'scrollTop',
    /** @type {'scrollLeft'} */ Left: 'scrollLeft',
};

/**
 * React hook to add a listener to a scroll event in a given direction. This keeps track of the last scroll position
 * and does not execute the callback if scrolling positions do not change (i.e., horizontal scrolling for a vertical
 * scroll listener).
 *
 * @param {ScrollDirection[keyof ScrollDirection]} direction The direction of scroll interested in. See the ScrollDirection export. This is
 *                 immutable and cannot be changed between renders.
 * @param {React.RefObject<React.ElementType>} ref
 * @param {Function} callback
 */
export default function useScrollListener(direction, ref, callback) {
    const lastValue = useRef(0);
    const scrollDirection = useRef(direction);
    const callbackRef = useRef();
    callbackRef.current = callback;

    const el = ref.current;

    useEffect(() => {
        if (!el) return;

        function handler(e) {
            const scrollValue = e.target[scrollDirection.current];

            if (scrollValue !== lastValue.current) {
                if (typeof callbackRef.current === 'function') {
                    callbackRef.current(scrollValue);
                }
                lastValue.current = scrollValue;
            }
        }

        el.addEventListener('scroll', handler);

        return () => {
            el.removeEventListener('scroll', handler);
        };
    }, [el]);
}
